import React, { ReactElement, ReactNode, useCallback, useMemo, useState } from 'react';
import { ISelectOption } from 'ui-toolkit';
import { useField } from 'formik';
import {
  IDisbursementFormData,
  isCheckDisbursementFormData,
  isACHDisbursementFormData,
  isWireDisbursementFormData,
} from 'types/order/escrow/IDisbursementFormData';
import { assertDefined } from 'utilities/Assertions';
import { useAppSelector } from 'store/store';
import { selectCurrentOrder } from 'selectors/order/selectCurrentOrder';
import { ItemLoading } from 'ui-toolkit/components/Loading/ItemLoading';
import classNames from 'classnames';
import { ICreateSettlementFeeForm } from 'types/order/fees/ICreateSettlementItemForm';
import { ConvertACHToWireModal } from '../convertACHToWire/ConvertACHToWireModal';
import { convertDisbursementType } from '../convertDisbursementType';
import { IDisbursementType } from '../IDisbursementType';
import { borderAndBackground } from './utils/borderAndBackground';
import { DisbursementHeader } from './components/DisbursementHeader';
import { DeleteConfirmationModal } from './components/DeleteConfirmationModal';
import { DisbursementTypeSelector } from './components/DisbursementTypeSelector';
import { useDisbursementCopyContext } from '../context/DisbursementCopyContext';
import { applyCopiedDataToForm } from '../utils/disbursementCopyUtils';
import { eDisbursementType } from 'types/order/escrow/eDisbursementType';
import { toast } from 'react-toastify';
import { useHasFeatureFlag } from '../../../../../../selectors/useHasFeatureFlag';

export interface IBaseDisbursementContainerProps {
  fee: ICreateSettlementFeeForm;
  feeName: string;
  children: ({ TypeChange }: { TypeChange: ReactElement; isCardCollapsed?: boolean }) => ReactNode;
  push: (x: IDisbursementFormData) => void;
  onRemove?: () => void;
  disbursements: IDisbursementFormData[];
  disbursementType: IDisbursementType;
  disbursementOptions: IDisbursementType[];
  isSubmitting: boolean;
  nameKey?: string;
  isDirty: boolean;
  hasErrors: boolean;
  isCollapsible?: boolean;
}

export const BaseDisbursementContainer: React.FC<IBaseDisbursementContainerProps> = ({
  children,
  fee,
  feeName,
  push,
  onRemove,
  disbursements,
  disbursementType,
  disbursementOptions,
  isSubmitting,
  nameKey,
  isDirty,
  hasErrors,
  isCollapsible = false,
}) => {
  const disbursementMethodOptions: ISelectOption[] = disbursementOptions.map((t) => ({ label: t.type, value: t.type }));
  const [{ value: data }, , { setValue: replace }] = useField<IDisbursementFormData>('value');
  const { data: order } = useAppSelector(selectCurrentOrder);
  const [, , { setValue, setTouched }] = useField<boolean | undefined>(`value.isDeleting`);
  const [prepareToDelete, setPrepareToDelete] = useState<boolean>(false);
  const [isTypeLocked, setIsTypeLocked] = useState<boolean>(true);
  const [isCardCollapsed, setIsCardCollapsed] = useState<boolean>(false);
  const [showConvertACHModal, setShowConvertACHModal] = useState<boolean>(false);
  const [showCopyCheckOptions, setShowCopyCheckOptions] = useState<boolean>(false);
  const isSpruceWithZillowEnabled = useHasFeatureFlag('pinecone.spruce-with-zillow-closing');
  const isZillowClosing = useHasFeatureFlag('pinecone.zillow-closing');
  const setDeleting = useCallback(async () => {
    await setValue(true);
    await setTouched(true);
  }, [setValue, setTouched]);

  const { copiedData } = useDisbursementCopyContext();

  const onDisbursementMethodChange = useCallback(
    async (newOption: ISelectOption) => {
      assertDefined(order);
      if (!data.isNew && data.type === eDisbursementType.ACH && newOption.value === eDisbursementType.Wire) {
        setShowConvertACHModal(true);
      } else {
        const newValue = convertDisbursementType(data, newOption.value, order, fee, isSpruceWithZillowEnabled, isZillowClosing);
        if (data.isNew) {
          await replace(newValue);
        }
        if (!data.isNew) {
          await setDeleting();
          push(newValue);
        }
      }

      if (newOption.value === eDisbursementType.Check || newOption.value === eDisbursementType.Wire) {
        setShowCopyCheckOptions(true);
      } else {
        setShowCopyCheckOptions(false);
      }
    },
    [
      order,
      data,
      fee,
      replace,
      setDeleting,
      push,
      setShowConvertACHModal,
      setShowCopyCheckOptions,
      isSpruceWithZillowEnabled,
      isZillowClosing,
    ]
  );

  const handlePasteData = useCallback(async () => {
    if (copiedData && (isCheckDisbursementFormData(data) || isWireDisbursementFormData(data))) {
      const updatedData = applyCopiedDataToForm(data, copiedData);
      await replace(updatedData);
      toast(`Beneficiary ${copiedData.name} info, has been pasted to the check`);
    }
  }, [copiedData, data, replace]);

  const isReadOnly = disbursementType.isReadOnly(data) || !disbursementType.hasEditPermission;
  const shouldShowCopyPasteOptions = useMemo(() => {
    return (
      showCopyCheckOptions ||
      disbursementType.type === eDisbursementType.Check ||
      disbursementType.type === eDisbursementType.Wire
    );
  }, [showCopyCheckOptions, disbursementType]);

  return (
    <div
      className={classNames(
        'relative my-4 w-full rounded-2xl border-[1px] p-4 shadow',
        borderAndBackground(isDirty, isReadOnly, hasErrors)
      )}
    >
      <DisbursementHeader
        feeName={feeName}
        data={data}
        disbursements={disbursements}
        fee={fee}
        isReadOnly={isReadOnly}
        isCollapsible={isCollapsible}
        isCardCollapsed={isCardCollapsed}
        onRemove={onRemove}
        showCopyPasteOptions={shouldShowCopyPasteOptions}
        onToggleCollapse={() => setIsCardCollapsed((prev) => !prev)}
        onDelete={() => setPrepareToDelete(true)}
        onPasteData={handlePasteData}
      />

      {children({
        TypeChange: (
          <DisbursementTypeSelector
            data={data}
            disbursementMethodOptions={disbursementMethodOptions}
            isTypeLocked={isTypeLocked}
            isReadOnly={isReadOnly}
            onTypeChange={onDisbursementMethodChange}
            onToggleLock={() => setIsTypeLocked(!isTypeLocked)}
          />
        ),
        isCardCollapsed,
      })}

      {prepareToDelete && (
        <DeleteConfirmationModal
          data={data}
          nameKey={nameKey}
          onConfirm={setDeleting}
          onCancel={() => setPrepareToDelete(false)}
        />
      )}

      {showConvertACHModal && isACHDisbursementFormData(data) && (
        <ConvertACHToWireModal ach={data} onClose={() => setShowConvertACHModal(false)} />
      )}

      <ItemLoading isLoading={isSubmitting} />
    </div>
  );
};
